import {
  AmbientLight,
  Clock,
  Color,
  DirectionalLight,
  MathUtils,
  Mesh,
  PerspectiveCamera,
  PointLight,
  Scene,
  ShaderMaterial,
  SphereGeometry,
  Vector3,
  WebGLRenderer,
  SRGBColorSpace
} from 'three';
import vertexShader from '../shaders/vertex.glsl';
import fragmentShader from '../shaders/fragment.glsl';

const DPR_BOUNDS = { min: 1, max: 1.5 };
const SPHERE_RADIUS = 0.28;
const SPHERE_SEGMENTS = 160;

const RENDERER_OPTIONS = {
  antialias: true,
  alpha: false,
  stencil: false,
  depth: false,
  powerPreference: 'high-performance'
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const clampPixelRatio = (value) => clamp(value, DPR_BOUNDS.min, DPR_BOUNDS.max);
const INITIAL_ROTATION = [0.12, 0.45, 0];
const COMPACT_ROTATION = [0.08, 0.32, 0];
const BASE_LIGHT_POSITION = new Vector3(0.8, 0.6, 1.6);
const IDLE_DISTORT = 0.28;
const REST_DISTORT = 0.2;
const ACTIVE_DISTORT = 1;
const ease = (rate, delta) => 1 - Math.pow(1 - rate, delta * 60);

const createShaderMaterial = () =>
  new ShaderMaterial({
    uniforms: {
      time: { value: 0 },
      uColorA: { value: new Color('#f3f5f9') },
      uColorB: { value: new Color('#121723') },
      uAccentColor: { value: new Color('#8aa8ff') },
      uDistort: { value: 0.28 },
      uGlow: { value: 0 }
    },
    vertexShader,
    fragmentShader
  });

export default class Sketch {
  constructor({ domElement }) {
    if (!domElement) {
      throw new Error('Sketch requires a valid container element');
    }

    this.container = domElement;
    this.clock = new Clock();
    this.scene = new Scene();

    this.camera = this.createCamera();
    this.renderer = this.createRenderer();
    this.addLights();
    this.mesh = this.createMesh();
    this.scene.add(this.mesh);

    const baseLightPosition = this.accentLight?.position.clone() ?? BASE_LIGHT_POSITION.clone();

    this.pointerState = {
      active: false,
      pointerId: null,
      targetRotation: new Vector3(...INITIAL_ROTATION),
      lightBasePosition: baseLightPosition,
      lightTarget: baseLightPosition.clone(),
      strength: 0,
      distortTarget: IDLE_DISTORT
    };

    this.colors = {
      accentBase: new Color('#7f96ff'),
      accentActive: new Color('#c9d7ff')
    };

    this.interactionTarget = this.container.closest('.site-footer__canvas-wrapper') ?? this.container;
    this.bindPointerEvents();

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this.container);
    this.handleResize();

    this.renderer.setAnimationLoop(this.render);
  }

  createCamera() {
    const camera = new PerspectiveCamera(45, 1, 0.1, 20);
    camera.position.set(0, 0, 1.6);
    return camera;
  }

  createRenderer() {
    const renderer = new WebGLRenderer(RENDERER_OPTIONS);
    renderer.outputColorSpace = SRGBColorSpace;
    renderer.setPixelRatio(clampPixelRatio(window.devicePixelRatio));
    renderer.setSize(1, 1, false);
    this.container.appendChild(renderer.domElement);
    return renderer;
  }

  createMesh() {
    const geometry = new SphereGeometry(SPHERE_RADIUS, SPHERE_SEGMENTS, SPHERE_SEGMENTS);
    const material = createShaderMaterial();
    const mesh = new Mesh(geometry, material);
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    return mesh;
  }

  addLights() {
    this.scene.add(new AmbientLight(0x2a3354, 0.6));

    const directionalLights = [
      { color: 0xb8c6ff, intensity: 1.25, position: [2.5, 3.5, 4.0], target: [0, 0, 0] },
      { color: 0x0b0f18, intensity: 0.7, position: [-2.0, -1.5, -3.0] }
    ];

    directionalLights.forEach(({ color, intensity, position, target }) => {
      const light = new DirectionalLight(color, intensity);
      light.position.set(...position);
      if (target) {
        light.target.position.set(...target);
        this.scene.add(light.target);
      }
      this.scene.add(light);
    });

    this.accentLight = new PointLight(0xaec4ff, 1, 6, 2);
    this.accentLight.position.set(0.8, 0.6, 1.6);
    this.scene.add(this.accentLight);
  }

  bindPointerEvents() {
    const target = this.interactionTarget;
    target.addEventListener('pointerdown', this.handlePointerDown);
    target.addEventListener('pointermove', this.handlePointerMove, { capture: true });
    target.addEventListener('pointerup', this.handlePointerUp, { capture: true });
    target.addEventListener('pointerleave', this.handlePointerLeave);
    target.addEventListener('pointercancel', this.handlePointerUp, { capture: true });
  }

  updatePointerState(event) {
    if (!this.interactionTarget) return;

    const rect = this.interactionTarget.getBoundingClientRect();
    const normalizedX = ((event.clientX - rect.left) / rect.width) - 0.5;
    const normalizedY = ((event.clientY - rect.top) / rect.height) - 0.5;

    const width = this.container.clientWidth || 1;
    const isCompact = width < 768 || rect.height < 400;
    const rangeX = isCompact ? 0.7 : 0.9;
    const rangeY = isCompact ? 1.2 : 1.6;
    const rangeZ = isCompact ? 0.4 : 0.6;

    const rotationX = MathUtils.clamp(-normalizedY * rangeX, -0.95, 0.95);
    const rotationY = MathUtils.clamp(normalizedX * rangeY, -1.6, 1.6);
    const rotationZ = MathUtils.clamp(normalizedX * normalizedY * rangeZ, -0.6, 0.6);

    this.pointerState.targetRotation.set(rotationX, rotationY, rotationZ);
    this.pointerState.distortTarget = MathUtils.clamp(
      Math.abs(normalizedX) + Math.abs(normalizedY),
      IDLE_DISTORT,
      ACTIVE_DISTORT
    );

    if (this.accentLight) {
      const lightRange = isCompact ? 0.6 : 0.9;
      this.pointerState.lightTarget.set(
        this.pointerState.lightBasePosition.x + normalizedX * lightRange,
        this.pointerState.lightBasePosition.y - normalizedY * (lightRange * 0.9),
        this.pointerState.lightBasePosition.z + normalizedY * 0.2
      );
    }

    this.pointerState.active = true;
  }

  handlePointerDown = (event) => {
    if (!this.interactionTarget) return;
    this.pointerState.pointerId = event.pointerId;
    this.pointerState.active = true;
    this.pointerState.distortTarget = ACTIVE_DISTORT;
    this.interactionTarget.setPointerCapture(event.pointerId);
    this.updatePointerState(event);
  };

  handlePointerMove = (event) => {
    if (this.pointerState.pointerId !== null && this.pointerState.pointerId !== event.pointerId) return;
    this.updatePointerState(event);
  };

  handlePointerUp = (event) => {
    if (this.pointerState.pointerId !== null && this.pointerState.pointerId !== event.pointerId) return;

    if (this.interactionTarget?.hasPointerCapture?.(event.pointerId)) {
      this.interactionTarget.releasePointerCapture(event.pointerId);
    }

    this.pointerState.pointerId = null;
    this.resetPointerState();
  };

  handlePointerLeave = () => {
    if (this.pointerState.pointerId !== null) return;
    this.resetPointerState();
  };

  resetPointerState() {
    this.pointerState.active = false;
    const width = this.container.clientWidth || 1;
    const isCompact = width < 768;
    this.pointerState.targetRotation.set(...(isCompact ? COMPACT_ROTATION : INITIAL_ROTATION));
    this.pointerState.lightTarget.copy(this.pointerState.lightBasePosition);
    this.pointerState.distortTarget = REST_DISTORT;
  }

  handleResize = () => {
    const width = this.container.clientWidth || 1;
    const height = this.container.clientHeight || 1;

    this.renderer.setPixelRatio(clampPixelRatio(window.devicePixelRatio));
    this.renderer.setSize(width, height, false);

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();

    const isCompact = width < 768 || height < 400;
    this.camera.position.setZ(isCompact ? 2.2 : 1.6);
    this.pointerState.targetRotation.set(...(isCompact ? COMPACT_ROTATION : INITIAL_ROTATION));
  };

  render = () => {
    const delta = this.clock.getDelta();

    const rotationBlend = ease(0.2, delta);
    this.mesh.rotation.x = MathUtils.lerp(this.mesh.rotation.x, this.pointerState.targetRotation.x, rotationBlend);
    this.mesh.rotation.y = MathUtils.lerp(this.mesh.rotation.y, this.pointerState.targetRotation.y, rotationBlend);
    this.mesh.rotation.z = MathUtils.lerp(this.mesh.rotation.z, this.pointerState.targetRotation.z, rotationBlend * 0.8);

    const uniforms = this.mesh.material.uniforms;
    if (uniforms?.time) {
      uniforms.time.value = this.clock.elapsedTime;
    }

    if (uniforms?.uAccentColor) {
      uniforms.uAccentColor.value.lerpColors(
        this.colors.accentBase,
        this.colors.accentActive,
        this.pointerState.strength
      );
    }

    const distortBlend = ease(0.22, delta);
    const distortValue = MathUtils.lerp(uniforms?.uDistort?.value ?? 0, this.pointerState.distortTarget, distortBlend);
    if (uniforms?.uDistort) {
      uniforms.uDistort.value = distortValue;
    }

    if (uniforms?.uGlow) {
      uniforms.uGlow.value = MathUtils.lerp(uniforms.uGlow.value, this.pointerState.strength, distortBlend * 0.6);
    }

    const intensityTarget = this.pointerState.active ? 1 : 0;
    const strengthBlend = ease(0.18, delta);
    this.pointerState.strength = MathUtils.lerp(this.pointerState.strength, intensityTarget, strengthBlend);

    this.accentLight.intensity = MathUtils.lerp(0.75, 1.6, this.pointerState.strength);
    this.accentLight.position.lerp(this.pointerState.lightTarget, strengthBlend * 0.9);

    this.renderer.render(this.scene, this.camera);
  };

}

new Sketch({
  domElement: document.getElementById('footer-sketch')
});
